import { createContext, useContext, useEffect, useRef, useState } from "react";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [video, setVideo] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audio.addEventListener("timeupdate", () => setProgress(audio.currentTime));
    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
    audio.addEventListener("ended", () => setPlaying(false));
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("play", () => setPlaying(true));
    audioRef.current = audio;
    return () => audio.pause();
  }, []);

  const play = (episode) => {
    const audio = audioRef.current;
    if (episode.video_id && !episode.audio_url) {
      audio.pause();
      setVideo(episode);
      return;
    }
    if (current?.id === episode.id) {
      playing ? audio.pause() : audio.play();
      return;
    }
    audio.src = episode.audio_url;
    setCurrent(episode);
    setProgress(0);
    audio.play().catch(() => {});
  };

  const toggle = () => current && play(current);
  const seek = (t) => { audioRef.current.currentTime = t; setProgress(t); };
  const skip = (delta) => seek(Math.max(0, Math.min(duration || 0, progress + delta)));
  const close = () => { audioRef.current.pause(); setCurrent(null); };
  const closeVideo = () => setVideo(null);
  const isPlaying = (id) => (playing && current?.id === id) || video?.id === id;

  return (
    <PlayerContext.Provider value={{ current, video, playing, progress, duration, play, toggle, seek, skip, close, closeVideo, isPlaying }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
